// =====================================================
// MANUAL TRADE ENTRY FORM - ADVANCED FEATURES
// Includes: Auto R:R calculation, Tags, Screenshot upload
// =====================================================

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiX, FiSave, FiTrendingUp, FiTrendingDown, FiDollarSign, FiClock, FiPlus, FiUpload, FiImage, FiTag, FiClipboard, FiMinus } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import type { MarketType, Direction, OutcomeStatus } from '@/types/journal';

interface ManualTradeFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MARKET_OPTIONS: { value: MarketType; label: string }[] = [
  { value: 'gold', label: 'Gold' },
  { value: 'indices', label: 'Indices' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'forex', label: 'Forex' },
];

const ASSET_PRESETS: Record<MarketType, string[]> = {
  gold: ['XAUUSD', 'XAUEUR', 'GC'],
  indices: ['US30', 'NAS100', 'SPX500', 'GER30'],
  crypto: ['BTC', 'ETH', 'SOL', 'XRP', 'ADA', 'DOGE'],
  forex: ['EURUSD', 'GBPUSD', 'USDJPY', 'AUDUSD'],
  other: [],
};

const OUTCOME_OPTIONS: { value: OutcomeStatus; label: string; color: string }[] = [
  { value: 'PENDING', label: 'Pending', color: 'text-amber-400' },
  { value: 'WIN', label: 'Win', color: 'text-emerald-400' },
  { value: 'LOSS', label: 'Loss', color: 'text-red-400' },
  { value: 'BREAKEVEN', label: 'Breakeven', color: 'text-blue-400' },
  { value: 'MISSED', label: 'Missed', color: 'text-purple-400' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'text-gray-400' },
];

// Preset tags
const SESSION_PRESETS = ['London Open', 'NY Session', 'Asia Session', 'London/NY Overlap', 'Pre-Market', 'After Hours'];
const PLAN_PRESETS = ['Trend Continuation', 'Range Breakout', 'Mean Reversion', 'Momentum', 'Scalp', 'Swing'];
const STRATEGY_PRESETS = ['EMA Bounce', 'Order Block', 'SMC', 'Fibonacci', 'Supply/Demand', 'Price Action', 'ICT'];

const ManualTradeForm: React.FC<ManualTradeFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state
  const [marketType, setMarketType] = useState<MarketType>('crypto');
  const [assetSymbol, setAssetSymbol] = useState('');
  const [customAsset, setCustomAsset] = useState('');
  const [direction, setDirection] = useState<Direction>('LONG');
  const [entryPrice, setEntryPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [takeProfit2, setTakeProfit2] = useState('');
  const [takeProfit3, setTakeProfit3] = useState('');
  const [showExtraTPs, setShowExtraTPs] = useState(false);
  const [outcomeStatus, setOutcomeStatus] = useState<OutcomeStatus>('PENDING');
  const [netProfit, setNetProfit] = useState('');
  const [notes, setNotes] = useState('');
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split('T')[0]);
  
  // New advanced features
  const [sessionTag, setSessionTag] = useState('');
  const [planTag, setPlanTag] = useState('');
  const [strategyTag, setStrategyTag] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [showTagsSection, setShowTagsSection] = useState(false);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Auto-calculate Risk/Reward
  const calculatedRR = useMemo(() => {
    const entry = parseFloat(entryPrice);
    const sl = parseFloat(stopLoss);
    const tp = parseFloat(takeProfit);
    
    if (isNaN(entry) || isNaN(sl) || isNaN(tp) || sl === entry) {
      return null;
    }
    
    let risk: number;
    let reward: number;
    
    if (direction === 'LONG') {
      risk = entry - sl;
      reward = tp - entry;
    } else {
      risk = sl - entry;
      reward = entry - tp;
    }
    
    if (risk <= 0) return null;
    
    return {
      risk: Math.abs(risk),
      reward: Math.abs(reward),
      ratio: Math.abs(reward) / Math.abs(risk)
    };
  }, [entryPrice, stopLoss, takeProfit, direction]);

  const resetForm = () => {
    setMarketType('crypto');
    setAssetSymbol('');
    setCustomAsset('');
    setDirection('LONG');
    setEntryPrice('');
    setStopLoss('');
    setTakeProfit('');
    setTakeProfit2('');
    setTakeProfit3('');
    setShowExtraTPs(false);
    setOutcomeStatus('PENDING');
    setNetProfit('');
    setNotes('');
    setTradeDate(new Date().toISOString().split('T')[0]);
    setSessionTag('');
    setPlanTag('');
    setStrategyTag('');
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setShowTagsSection(false);
    setError(null);
  };

  // Unified file validation and processing
  const processScreenshotFile = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG)');
      return false;
    }
    
    // Validate file size (8MB max)
    if (file.size > 8 * 1024 * 1024) {
      setError('Screenshot must be less than 8MB');
      return false;
    }
    
    setScreenshotFile(file);
    setScreenshotPreview(URL.createObjectURL(file));
    setError(null);
    return true;
  }, []);

  // Handle file input change
  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processScreenshotFile(file);
  };

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) processScreenshotFile(file);
  }, [processScreenshotFile]);

  // Handle Ctrl+V paste
  useEffect(() => {
    if (!isOpen) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            processScreenshotFile(file);
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, processScreenshotFile]);

  const uploadScreenshot = async (userId: string): Promise<string | null> => {
    if (!screenshotFile) return null;
    
    setIsUploadingScreenshot(true);
    try {
      const fileExt = screenshotFile.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('trade-screenshots')
        .upload(fileName, screenshotFile);

      if (uploadError) {
        console.error('Screenshot upload error:', uploadError);
        return null;
      }

      // Bucket is private (security): store the object PATH, not a public URL.
      // Display resolves a short-lived signed URL via resolveTradeScreenshotUrl().
      return fileName;
    } catch (err) {
      console.error('Screenshot upload failed:', err);
      return null;
    } finally {
      setIsUploadingScreenshot(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to add trades');
        return;
      }

      const finalAsset = assetSymbol === 'custom' ? customAsset.toUpperCase() : assetSymbol;
      
      if (!finalAsset) {
        setError('Please select or enter an asset symbol');
        return;
      }

      // Use calculated R:R or compute it
      let riskReward: number | null = calculatedRR?.ratio ?? null;
      const entry = parseFloat(entryPrice);
      const sl = parseFloat(stopLoss);
      const tp = parseFloat(takeProfit);
      
      if (!riskReward && !isNaN(entry) && !isNaN(sl) && !isNaN(tp) && sl !== entry) {
        const risk = Math.abs(entry - sl);
        const reward = Math.abs(tp - entry);
        riskReward = reward / risk;
      }

      // Parse net profit - ensure LOSS is always negative, WIN is always positive
      let profit = netProfit ? parseFloat(netProfit) : null;
      if (profit !== null) {
        if (outcomeStatus === 'LOSS') {
          profit = -Math.abs(profit);
        } else if (outcomeStatus === 'WIN') {
          profit = Math.abs(profit);
        }
      }

      // Upload screenshot if present
      let screenshotUrl: string | null = null;
      if (screenshotFile) {
        screenshotUrl = await uploadScreenshot(user.id);
      }

      // Create the journal entry
      const { error: insertError } = await supabase
        .from('journal_entries')
        .insert({
          user_id: user.id,
          market_type: marketType,
          asset_symbol: finalAsset,
          execution_permission: 'TRADE',
          direction: direction,
          entry_price: !isNaN(entry) ? entry : null,
          stop_loss: !isNaN(sl) ? sl : null,
          take_profit_1: !isNaN(tp) ? tp : null,
          take_profit_2: takeProfit2 ? parseFloat(takeProfit2) : null,
          take_profit_3: takeProfit3 ? parseFloat(takeProfit3) : null,
          risk_reward: riskReward,
          outcome_status: outcomeStatus,
          outcome_r_multiple: profit,
          outcome_notes: notes || null,
          raw_analysis_text: `Manual trade entry - ${direction} ${finalAsset}`,
          model_used: 'Manual Entry',
          chart_count: 0,
          created_at: tradeDate === new Date().toISOString().split('T')[0] 
            ? new Date().toISOString() 
            : new Date(tradeDate + 'T12:00:00').toISOString(),
          outcome_updated_at: outcomeStatus !== 'PENDING' ? new Date().toISOString() : null,
          // New advanced fields
          session_tag: sessionTag || null,
          plan_tag: planTag || null,
          strategy_tag: strategyTag || null,
          screenshot_url: screenshotUrl,
        });

      if (insertError) {
        console.error('Error creating manual trade:', insertError);
        setError('Failed to save trade. Please try again.');
        return;
      }

      console.log('✅ Manual trade saved successfully');
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving manual trade:', err);
      setError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-gray-900 border border-purple-500/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-800">
            <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              Add Manual Trade
            </h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-5 space-y-5">
            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}

            {/* Trade Date */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Trade Date</label>
              <div className="relative">
                <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={tradeDate}
                  onChange={(e) => setTradeDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Market Type */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Market</label>
              <div className="grid grid-cols-4 gap-2">
                {MARKET_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setMarketType(opt.value);
                      setAssetSymbol('');
                    }}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      marketType === opt.value
                        ? 'bg-gray-900/80 border border-cyan-500/50 text-cyan-400'
                        : 'bg-gray-900/60 border border-purple-500/20 text-gray-400 hover:border-cyan-500/40 hover:text-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Asset Symbol */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Asset</label>
              <select
                value={assetSymbol}
                onChange={(e) => setAssetSymbol(e.target.value)}
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select asset...</option>
                {ASSET_PRESETS[marketType].map((asset) => (
                  <option key={asset} value={asset}>{asset}</option>
                ))}
                <option value="custom">Other (Custom)</option>
              </select>
              
              {assetSymbol === 'custom' && (
                <input
                  type="text"
                  placeholder="Enter symbol (e.g., AAPL)"
                  value={customAsset}
                  onChange={(e) => setCustomAsset(e.target.value)}
                  className="w-full mt-2 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              )}
            </div>

            {/* Direction */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Direction</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDirection('LONG')}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
                    direction === 'LONG'
                      ? 'bg-gray-900/80 border border-emerald-500/50 text-emerald-400'
                      : 'bg-gray-900/60 border border-purple-500/20 text-gray-400 hover:border-cyan-500/40'
                  }`}
                >
                  <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
                  LONG
                </button>
                <button
                  type="button"
                  onClick={() => setDirection('SHORT')}
                  className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all ${
                    direction === 'SHORT'
                      ? 'bg-gray-900/80 border border-red-500/50 text-red-400'
                      : 'bg-gray-900/60 border border-purple-500/20 text-gray-400 hover:border-cyan-500/40'
                  }`}
                >
                  <SafeIcon icon={FiTrendingDown} className="w-4 h-4" />
                  SHORT
                </button>
              </div>
            </div>

            {/* Price Levels */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Entry Price</label>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={entryPrice}
                  onChange={(e) => setEntryPrice(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Stop Loss</label>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Take Profit 1</label>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Auto R:R Calculation Display */}
            {calculatedRR && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/30"
              >
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-semibold text-gray-400 uppercase">Auto-Calculated R:R</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-red-400">${calculatedRR.risk.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">Risk</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-emerald-400">${calculatedRR.reward.toFixed(2)}</div>
                    <div className="text-xs text-gray-500">Reward</div>
                  </div>
                  <div>
                    <div className={`text-lg font-bold ${calculatedRR.ratio >= 2 ? 'text-emerald-400' : calculatedRR.ratio >= 1 ? 'text-amber-400' : 'text-red-400'}`}>
                      1:{calculatedRR.ratio.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500">R:R Ratio</div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Optional TP2/TP3 Toggle */}
            {!showExtraTPs && (
              <button
                type="button"
                onClick={() => setShowExtraTPs(true)}
                className="text-xs text-purple-400 hover:text-cyan-400 transition-colors flex items-center gap-1"
              >
                <SafeIcon icon={FiPlus} className="w-3 h-3" />
                Add TP2 & TP3 (Optional)
              </button>
            )}

            {/* Extra Take Profit Levels */}
            {showExtraTPs && (
              <div className="p-4 rounded-xl bg-gray-800/30 border border-gray-700">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-300">Additional Take Profits</span>
                  <button
                    type="button"
                    onClick={() => setShowExtraTPs(false)}
                    className="p-1 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
                  >
                    <SafeIcon icon={FiMinus} className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 font-medium">Take Profit 2</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      value={takeProfit2}
                      onChange={(e) => setTakeProfit2(e.target.value)}
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1.5 font-medium">Take Profit 3</label>
                    <input
                      type="number"
                      step="any"
                      placeholder="0.00"
                      value={takeProfit3}
                      onChange={(e) => setTakeProfit3(e.target.value)}
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Tags Section Toggle */}
            {!showTagsSection && (
              <button
                type="button"
                onClick={() => setShowTagsSection(true)}
                className="text-xs text-purple-400 hover:text-cyan-400 transition-colors flex items-center gap-1"
              >
                <SafeIcon icon={FiTag} className="w-3 h-3" />
                Add Session / Plan / Strategy Tags (Optional)
              </button>
            )}

            {/* Tags Section */}
            {showTagsSection && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4 p-4 rounded-xl bg-gray-800/30 border border-gray-700"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiTag} className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium text-gray-300">Trade Tags</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTagsSection(false)}
                    className="p-1 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
                  >
                    <SafeIcon icon={FiMinus} className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Session Tag */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Session Tag</label>
                  <select
                    value={sessionTag}
                    onChange={(e) => setSessionTag(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select session...</option>
                    {SESSION_PRESETS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                {/* Plan Tag */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Plan Tag</label>
                  <select
                    value={planTag}
                    onChange={(e) => setPlanTag(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select plan...</option>
                    {PLAN_PRESETS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                {/* Strategy Tag */}
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5 font-medium">Strategy Tag</label>
                  <select
                    value={strategyTag}
                    onChange={(e) => setStrategyTag(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  >
                    <option value="">Select strategy...</option>
                    {STRATEGY_PRESETS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </motion.div>
            )}

            {/* Screenshot Upload with Drag & Drop and Ctrl+V */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Screenshot (Optional)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleScreenshotChange}
                className="hidden"
              />
              {!screenshotPreview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`w-full py-6 border-2 border-dashed rounded-xl cursor-pointer transition-all flex flex-col items-center justify-center gap-2 ${
                    isDragging 
                      ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400' 
                      : 'border-gray-700 text-gray-400 hover:border-purple-500/50 hover:text-purple-400'
                  }`}
                >
                  <SafeIcon icon={isDragging ? FiImage : FiUpload} className="w-6 h-6" />
                  <span className="text-sm font-medium">
                    {isDragging ? 'Drop your image here' : 'Upload Chart Screenshot'}
                  </span>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiUpload} className="w-3 h-3" />
                      Click
                    </span>
                    <span>•</span>
                    <span>Drag & Drop</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <SafeIcon icon={FiClipboard} className="w-3 h-3" />
                      Ctrl+V
                    </span>
                  </div>
                  <span className="text-xs text-gray-600 mt-1">PNG/JPG, max 8MB</span>
                </div>
              ) : (
                <div className="relative">
                  <img 
                    src={screenshotPreview} 
                    alt="Screenshot preview" 
                    className="w-full h-40 object-cover rounded-xl border border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setScreenshotFile(null);
                      setScreenshotPreview(null);
                    }}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-gray-900/80 border border-gray-700 text-gray-400 hover:text-red-400 hover:border-red-500/50 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-1 rounded-lg bg-gray-900/80 border border-gray-700 text-xs text-emerald-400">
                    ✓ Screenshot ready
                  </div>
                </div>
              )}
            </div>

            {/* Outcome */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Outcome</label>
              <div className="grid grid-cols-3 gap-2">
                {OUTCOME_OPTIONS.slice(0, 3).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setOutcomeStatus(opt.value)}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      outcomeStatus === opt.value
                        ? `bg-gray-900/80 border border-cyan-500/50 ${opt.color}`
                        : 'bg-gray-900/60 border border-purple-500/20 text-gray-400 hover:border-cyan-500/40'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {OUTCOME_OPTIONS.slice(3).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setOutcomeStatus(opt.value)}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                      outcomeStatus === opt.value
                        ? `bg-gray-900/80 border border-cyan-500/50 ${opt.color}`
                        : 'bg-gray-900/60 border border-purple-500/20 text-gray-400 hover:border-cyan-500/40'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Net Profit (shown if outcome is WIN, LOSS, or BREAKEVEN) */}
            {['WIN', 'LOSS', 'BREAKEVEN'].includes(outcomeStatus) && (
              <div>
                <label className="block text-xs text-gray-400 mb-1.5 font-medium">Net Profit / Net Loss ($)</label>
                <div className="relative">
                  <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="number"
                    step="any"
                    placeholder="Enter amount (e.g., 500 or -200)"
                    value={netProfit}
                    onChange={(e) => setNetProfit(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Enter the actual dollar amount of your profit or loss</p>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-medium">Notes (Optional)</label>
              <textarea
                placeholder="Trade notes, reasoning, lessons learned..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || isUploadingScreenshot}
              className="w-full py-3 rounded-xl bg-gray-900/80 border border-purple-500/30 hover:border-cyan-500/50 text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting || isUploadingScreenshot ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  {isUploadingScreenshot ? 'Uploading...' : 'Saving...'}
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-4 h-4 text-cyan-400" />
                  <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Save Trade</span>
                </>
              )}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ManualTradeForm;
